import {useMemo, useState} from 'react'
import {useDialogStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {AxiosError} from 'axios'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Download01Icon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {buildFile, downloadFile} from '@/utilities/helpers'
import {usePermissions} from '@/features/auth/store/store'
import {SiarBulkExportType, SiarExportProgressStatus} from '@/features/projects/siar-bulk-export/types'
import {NoEligibleProjectsError} from '@/features/projects/siar-bulk-export/http/siarBulkExport.http'
import {useStoreSiarBulkExport} from '@/features/projects/siar-bulk-export/queries/useStoreSiarBulkExport'
import {useGetSiarBulkExportPreview} from '@/features/projects/siar-bulk-export/queries/useGetSiarBulkExportPreview'
import {useGetSiarBulkExportPreviewCounters} from '@/features/projects/siar-bulk-export/queries/useGetSiarBulkExportPreviewCounters'
import {siarExportTypeToNoEligibleProjectsLabel} from '@/features/projects/siar-bulk-export/utils'
import {
    BulkExportConfig,
    BulkSiarExportConfigStep
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportConfigStep'
import {BulkSiarExportPreviewStep} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportPreviewStep'
import {BulkSiarExportResultStep} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportResultStep'

type DialogStep = 'config' | 'preview' | 'result'

export type PreviewFilterTab = 'all' | 'to_export' | 'to_transmit'

const tabToStatus: Record<PreviewFilterTab, SiarExportProgressStatus | undefined> = {
    all: undefined,
    to_export: 'to_export',
    to_transmit: 'to_transmit'
}

const initialConfig: BulkExportConfig = {
    exportType: null,
    dateFrom: null,
    dateTo: null,
    addRegistry: false
}

const formatPayloadDate = (date: Date | null): string | undefined =>
    date ? dayjs(date).format('YYYY-MM-DD') : undefined

const buildExportFileName = (exportType: SiarBulkExportType, dateFrom: Date | null, dateTo: Date | null): string => {
    const prefix = exportType === 'open_project' ? 'SIAR-Apertura' : 'SIAR-Chiusura'
    const fmt = (d: Date) => dayjs(d).format('DDMMYYYY')
    if (dateFrom && dateTo) return `${prefix}-${fmt(dateFrom)}-${fmt(dateTo)}`
    if (dateFrom) return `${prefix}-${fmt(dateFrom)}`
    if (dateTo) return `${prefix}-${fmt(dateTo)}`
    return prefix
}

type BulkSiarExportDialogProps = {
    onShowDetail?: (exportId: string) => void
}

export const BulkSiarExportDialog = ({onShowDetail}: BulkSiarExportDialogProps = {}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: setIsOpen
    })

    if (!permissions?.projects?.write) {
        return null
    }

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    <Button variant="primaryNeutral" onClick={() => setIsOpen(true)}>
                        <Download01Icon />
                        {t('projects:bulk_siar_export_title')}
                    </Button>
                }
            />
            {isOpen && (
                <BulkSiarExportDialogShell onClose={() => dialogStore.setOpen(false)} onShowDetail={onShowDetail} />
            )}
        </DialogProvider>
    )
}

const BulkSiarExportDialogShell = ({
    onClose,
    onShowDetail
}: {
    onClose: () => void
    onShowDetail?: (exportId: string) => void
}) => {
    const {t} = useTranslation()
    const [step, setStep] = useState<DialogStep>('config')
    const [config, setConfig] = useState<BulkExportConfig>(initialConfig)
    const [activeTab, setActiveTab] = useState<PreviewFilterTab>('all')
    // selectAll=true → ids holds excluded project IDs (send as exceptedProjectIds)
    // selectAll=false → ids holds included project IDs (send as selectedProjectIds)
    const [selectAll, setSelectAll] = useState(true)
    const [ids, setIds] = useState<Set<string>>(new Set())
    const [pendingCount, setPendingCount] = useState(0)
    const [createdExportId, setCreatedExportId] = useState<string>('')
    const [createdFileName, setCreatedFileName] = useState<string>('')

    const isPreviewReady = step === 'preview' && !!config.exportType && (!!config.dateFrom || !!config.dateTo)

    const previewQuery = useGetSiarBulkExportPreview(
        {
            exportType: config.exportType ?? 'open_project',
            dateFrom: formatPayloadDate(config.dateFrom),
            dateTo: formatPayloadDate(config.dateTo),
            status: tabToStatus[activeTab]
        },
        {
            enabled: isPreviewReady
        }
    )

    const countersQuery = useGetSiarBulkExportPreviewCounters(
        {
            exportType: config.exportType ?? 'open_project',
            dateFrom: formatPayloadDate(config.dateFrom),
            dateTo: formatPayloadDate(config.dateTo)
        },
        {
            enabled: isPreviewReady
        }
    )

    const eligibleTotal = countersQuery.data ? countersQuery.data.toExport + countersQuery.data.toTransmit : 0
    const isSelected = (projectId: string) => (selectAll ? !ids.has(projectId) : ids.has(projectId))
    const selectedCount = selectAll ? Math.max(0, eligibleTotal - ids.size) : ids.size
    const inOtherBatchProjectIds = countersQuery.data?.inOtherBatchProjectIds ?? []
    const selectedInOtherBatchCount = inOtherBatchProjectIds.filter(id => isSelected(id)).length
    const mutation = useStoreSiarBulkExport({
        onSuccess: data => {
            if (!config.exportType) return
            const baseName = buildExportFileName(config.exportType, config.dateFrom, config.dateTo)
            const file = buildFile({
                extension: 'zip',
                fileName: baseName,
                data: data.blob
            })
            downloadFile(file)
            if (data.skippedProjectIds.length > 0) {
                toast.info(
                    t('projects:bulk_siar_export_skipped_toast', {
                        count: data.skippedProjectIds.length
                    })
                )
            }
            const submittedCount = selectAll ? Math.max(0, eligibleTotal - ids.size) : ids.size
            setPendingCount(Math.max(0, submittedCount - data.skippedProjectIds.length))
            setCreatedExportId(data.exportId)
            setCreatedFileName(`${baseName}.zip`)
            setStep('result')
        },
        onError: (error: AxiosError<NoEligibleProjectsError>) => {
            const body = error.response?.data
            if (body?.code === 'NO_ELIGIBLE_PROJECTS' && config.exportType) {
                toast.error(t(siarExportTypeToNoEligibleProjectsLabel[config.exportType]))
                return
            }
            toast.error(t('errors:default'))
        }
    })

    // Toggles a single item's membership in the ids set.
    // When selectAll=true ids is the excluded set; when false ids is the included set.
    // The toggle logic is identical in both cases: flip membership.
    const handleToggle = (projectId: string) => {
        setIds(prev => {
            const next = new Set(prev)
            if (next.has(projectId)) next.delete(projectId)
            else next.add(projectId)
            return next
        })
    }

    const handleSelectMany = (projectIds: string[]) => {
        setIds(prev => {
            const next = new Set(prev)
            if (selectAll) {
                for (const id of projectIds) next.delete(id) // remove from excluded → included
            } else {
                for (const id of projectIds) next.add(id) // add to included
            }
            return next
        })
    }

    const handleDeselectMany = (projectIds: string[]) => {
        setIds(prev => {
            const next = new Set(prev)
            if (selectAll) {
                for (const id of projectIds) next.add(id) // add to excluded
            } else {
                for (const id of projectIds) next.delete(id) // remove from included → excluded
            }
            return next
        })
    }

    const handleMasterToggle = () => {
        setSelectAll(prev => !prev)
        setIds(new Set())
    }

    const handleIncludeOtherBatch = () => {
        handleSelectMany(inOtherBatchProjectIds)
    }

    const handleIgnoreOtherBatch = () => {
        handleDeselectMany(inOtherBatchProjectIds)
    }

    const handleGoToPreview = () => {
        setSelectAll(true)
        setIds(new Set())
        setActiveTab('all')
        setStep('preview')
    }

    const handleBackToConfig = () => {
        setStep('config')
    }

    const handleExport = () => {
        if (!config.exportType) return
        mutation.mutate({
            exportType: config.exportType,
            dateFrom: formatPayloadDate(config.dateFrom),
            dateTo: formatPayloadDate(config.dateTo),
            selectedProjectIds: selectAll ? [] : Array.from(ids),
            exceptedProjectIds: selectAll ? Array.from(ids) : [],
            addRegistry: config.addRegistry
        })
    }

    const dialogWidth = useMemo(() => (step === 'preview' ? '960px' : '670px'), [step])

    return (
        <Dialog width={dialogWidth}>
            <DialogHeader>
                <DialogHeading>{t('projects:bulk_siar_export_title')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            {step === 'config' && (
                <BulkSiarExportConfigStep config={config} onChange={setConfig} onNext={handleGoToPreview} />
            )}
            {step === 'preview' && (
                <BulkSiarExportPreviewStep
                    config={config}
                    items={previewQuery.remappedData}
                    total={previewQuery.total}
                    isLoading={previewQuery.isLoading}
                    counters={countersQuery.data}
                    isCountersLoading={countersQuery.isLoading}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    isSelected={isSelected}
                    selectedCount={selectedCount}
                    onMasterToggle={handleMasterToggle}
                    onToggle={handleToggle}
                    onIncludeOtherBatch={handleIncludeOtherBatch}
                    onIgnoreOtherBatch={handleIgnoreOtherBatch}
                    selectedInOtherBatchCount={selectedInOtherBatchCount}
                    onLoadMore={() => {
                        if (previewQuery.hasNextPage && !previewQuery.isFetchingNextPage) {
                            void previewQuery.fetchNextPage()
                        }
                    }}
                    onBack={handleBackToConfig}
                    onExport={handleExport}
                    isExportPending={mutation.isPending}
                />
            )}
            {step === 'result' && (
                <BulkSiarExportResultStep
                    pendingCount={pendingCount}
                    fileName={createdFileName}
                    onClose={onClose}
                    onGoToGroup={
                        onShowDetail && createdExportId
                            ? () => {
                                  onClose()
                                  onShowDetail(createdExportId)
                              }
                            : undefined
                    }
                />
            )}
        </Dialog>
    )
}
